import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { HrInfoValues } from '../../../schemas/employee-form-schema';
import {
  activeIndexAfterRemoval,
  canActivateTerm,
  canRemoveTerm,
  createTermFrom,
} from '../../../schemas/hr-term-schema';
import { HrTermCard } from './hr-term-card';

/**
 * "HR Info": one collapsible card per HR term of the employee, each with its own period, working days and
 * leave allotment. Exactly one term is active; an employee gets a new term (prefilled from the last one)
 * whenever a new HR period starts.
 */
export const EmploymentStep = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { control, getValues, setValue, watch, clearErrors, formState } =
    useFormContext<HrInfoValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'HrTerms' });

  const activeTermIndex = watch('ActiveTermIndex');
  const terms = watch('HrTerms');
  const [termToActivate, setTermToActivate] = useState<number | null>(null);

  const handleAddTerm = () => {
    const allTerms = getValues('HrTerms');
    append(createTermFrom(allTerms[allTerms.length - 1]));
  };

  const handleRemoveTerm = (index: number) => {
    setValue('ActiveTermIndex', activeIndexAfterRemoval(getValues('ActiveTermIndex'), index));
    remove(index);
  };

  const handleActivateConfirm = () => {
    if (termToActivate === null) return;
    setValue('ActiveTermIndex', termToActivate, { shouldDirty: true });
    clearErrors('ActiveTermIndex');
    toast({
      title: t('HR_TERM_ACTIVATED_SUCCESSFULLY', {
        number: termToActivate + 1,
        defaultValue: 'HR Term {{number}} has been activated successfully',
      }),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {fields.map((field, index) => (
        <HrTermCard
          key={field.id}
          index={index}
          isActive={index === activeTermIndex}
          canActivate={canActivateTerm(index, activeTermIndex)}
          canRemove={Boolean(terms[index]) && canRemoveTerm(terms[index], fields.length)}
          onActivate={() => setTermToActivate(index)}
          onRemove={() => handleRemoveTerm(index)}
          defaultOpen={index === activeTermIndex || !terms[index]?.ItemId}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        className="w-full border-dashed sm:w-auto sm:self-start"
        onClick={handleAddTerm}
      >
        <Plus className="size-4" />
        {t('ADD_ANOTHER_TERM')}
      </Button>

      {formState.errors.ActiveTermIndex && (
        <p className="text-[0.8rem] font-medium text-destructive">
          {formState.errors.ActiveTermIndex.message}
        </p>
      )}

      <ConfirmationModal
        open={termToActivate !== null}
        onOpenChange={(open) => !open && setTermToActivate(null)}
        title={`${t('HR_TERM')} ${(termToActivate ?? 0) + 1}`}
        description={t('DO_YOU_CONFIRM_TO_ACTIVATE_HR_TERM')}
        confirmText={t('YES')}
        onConfirm={handleActivateConfirm}
      />
    </div>
  );
};
